import React, { useState, useEffect, useCallback } from 'react';
import { MessageCircle, Phone, Video, Send, Paperclip, Smile, MoreVertical } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import ContactsSidebar from './components/ContactsSidebar';
import ConversationView from './components/ConversationView';
import UserDetailsPanel from './components/UserDetailsPanel';

const PrivateMessaging = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [contacts, setContacts] = useState([]);
  // const [loading, setLoading] = useState(true); // retiré (non utilisé)
  const [newMessage, setNewMessage] = useState('');
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [onlineUsers] = useState(new Set());
  const [unreadCounts, setUnreadCounts] = useState({});
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
  getCurrentUser(); 
  }, []);

  // Realtime subscription messages (simplifié)
  useEffect(() => {
    if (!selectedConversation) return;
    const channel = supabase.channel(`conv:${selectedConversation.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `conversation_id=eq.${selectedConversation.id}` }, payload => {
        setMessages(prev => [...prev, payload.new]);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [selectedConversation]);

  async function getCurrentUser() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase.from('user_profiles').select('*').eq('id', user.id).single();
        setCurrentUser(profile);
      }
    } catch (error) {
      console.error('Error getting current user:', error);
    }
  }

  const fetchContacts = useCallback(async function fetchContacts() {
    try {
      // Ensure we have a current user before making the query
  if (!currentUser?.id) { 
        return;
      }
      const { data, error } = await supabase.from('user_profiles').select(`
          *,
          partners (
            id,
            partner_type,
            business_name,
            is_verified,
            average_rating
          )
        `).neq('id', currentUser.id);

      if (error) throw error;
      setContacts(data || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
  }
  }, [currentUser]);

  const fetchConversations = useCallback(async function fetchConversations() {
    try {
      // Ensure we have a current user before making the query
      if (!currentUser?.id) {
        return;
      }
      const { data, error } = await supabase.from('conversation_participants').select(`
          conversation_id,
          conversations (
            id,
            type,
            name,
            updated_at,
            conversation_participants (
              user_id,
              user_profiles (
                id,
                full_name,
                avatar_url,
                partners (
                  partner_type,
                  business_name
                )
              )
            )
          )
        `).eq('user_id', currentUser.id);

      if (error) throw error;

      const conversationData = data?.map(item => ({
        ...item?.conversations,
        participants: item?.conversations?.conversation_participants?.filter(p => p?.user_id !== currentUser?.id)
      })) || [];

      // Get unread counts for each conversation
      const unreadPromises = conversationData?.map(async (conv) => {
        const { data: count } = await supabase.rpc('get_unread_messages_count', { 
          conversation_uuid: conv.id 
        });
        return { [conv?.id]: count || 0 };
      });

      const unreadResults = await Promise.all(unreadPromises);
      const unreadMap = unreadResults?.reduce((acc, curr) => ({ ...acc, ...curr }), {});
      setUnreadCounts(unreadMap);

      setConversations(conversationData);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, [currentUser]);

  const fetchMessages = useCallback(async function fetchMessages() {
    if (!selectedConversation) return;

    try {
  const { data, error } = await supabase.from('messages').select(`
          *,
          sender:user_profiles!sender_id (
            id,
            full_name,
            avatar_url
          ),
          reply_to_message:messages!reply_to (
            id,
            content,
            sender:user_profiles!sender_id (
              full_name
            )
          ),
          message_reactions (
            emoji,
            user_id,
            user_profiles (
              full_name
            )
          )
  `).eq('conversation_id', selectedConversation.id).order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [selectedConversation]);

  const markAsRead = useCallback(async function markAsRead() {
    if (!selectedConversation) return;

    try {
      await supabase.rpc('mark_conversation_as_read', {
        conversation_uuid: selectedConversation.id
      });
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  }, [selectedConversation]);

  // Effect de suivi des messages (placé après définitions pour éviter TDZ)
  useEffect(() => {
    if (selectedConversation) {
      fetchMessages();
      markAsRead();
    }
  }, [selectedConversation, fetchMessages, markAsRead]);

  // Charger contacts & conversations quand currentUser acquis
  useEffect(() => {
    if (currentUser?.id) {
      fetchContacts();
      fetchConversations();
    }
  }, [currentUser, fetchContacts, fetchConversations]);

  const sendMessage = async () => {
    if (!newMessage?.trim() || !selectedConversation || !currentUser) return;

    try {
      const messageData = {
        conversation_id: selectedConversation?.id,
        sender_id: currentUser?.id,
        content: newMessage?.trim(),
        message_type: 'text'
      };

  const { error } = await supabase.from('messages').insert([messageData]);

      if (error) throw error;

      setNewMessage('');
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const startConversation = async (contact) => {
    if (!currentUser) return;

    try {
      // Check if conversation already exists
  const { data: existing } = await supabase.from('conversation_participants').select('conversation_id').eq('user_id', currentUser.id);

      if (existing?.length) {
        for (const conv of existing) {
          const { data: otherParticipant } = await supabase.from('conversation_participants').select('user_id').eq('conversation_id', conv.conversation_id).eq('user_id', contact.id);

          if (otherParticipant?.length) {
            // Conversation exists, select it
            const { data: conversation } = await supabase.from('conversations').select('*').eq('id', conv.conversation_id).single();
            
            setSelectedConversation(conversation);
            return;
          }
        }
      }

      // Create new conversation
      const { data: newConversation, error: convError } = await supabase.from('conversations').insert([{
          type: 'direct',
          created_by: currentUser.id
        }]).select().single();

      if (convError) throw convError;

      // Add participants
      const { error: participantsError } = await supabase.from('conversation_participants').insert([
          { conversation_id: newConversation.id, user_id: currentUser.id },
          { conversation_id: newConversation.id, user_id: contact.id }
        ]);

      if (participantsError) throw participantsError;

      setSelectedConversation(newConversation);
      fetchConversations();
    } catch (error) {
      console.error('Error starting conversation:', error);
    }
  };

  const filteredContacts = contacts?.filter(contact =>
    contact?.full_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    contact?.partners?.[0]?.business_name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  ) || [];

  const getRoleIcon = (role) => {
    switch (role) {
      case 'agent': return '🎵';
      case 'venue': return '🏛️';
      case 'booker': return '📅';
      case 'admin': return '⚡';
      default: return '👤';
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp)?.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 transition-colors">
      <div className="flex h-screen">
        {/* Left Sidebar - Contacts & Conversations */}
        <ContactsSidebar
          conversations={conversations}
          contacts={filteredContacts}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedConversation={selectedConversation}
          onSelectConversation={setSelectedConversation}
          onStartConversation={startConversation}
          unreadCounts={unreadCounts}
          onlineUsers={onlineUsers}
          getRoleIcon={getRoleIcon}
          formatTime={formatTime}
        />

        {/* Main Chat Area */}
    <div className="flex-1 flex flex-col bg-white dark:bg-neutral-900/60 backdrop-blur supports-[backdrop-filter]:bg-white/60 dark:supports-[backdrop-filter]:bg-neutral-900/70 transition-colors">
          {selectedConversation ? (
            <>
              {/* Chat Header */}
      <div className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 px-6 py-4 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex -space-x-2">
                      {selectedConversation?.participants?.slice(0, 2)?.map((participant, index) => (
                        <img
                          key={participant?.user_profiles?.id}
                          src={participant?.user_profiles?.avatar_url || `/api/placeholder/32/32`}
                          alt={participant?.user_profiles?.full_name}
                          className={`w-8 h-8 rounded-full border-2 border-white ${index === 1 ? 'ml-2' : ''}`}
                        />
                      ))}
                    </div>
                    <div>
                      <h2 className="font-semibold text-gray-900">
                        {selectedConversation?.name || 
                          selectedConversation?.participants?.[0]?.user_profiles?.full_name ||
                          'Conversation'
                        }
                      </h2>
                      <p className="text-sm text-gray-500">
                        {selectedConversation?.participants?.[0]?.user_profiles?.partners?.[0]?.business_name || 'En ligne'}
                      </p>
                    </div>
                  </div>
                    <div className="flex items-center space-x-2">
                    <button className="p-2 text-gray-400 dark:text-neutral-400 hover:text-gray-600 dark:hover:text-neutral-200 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg transition-colors">
                      <Phone className="w-5 h-5" />
                    </button>
                    <button className="p-2 text-gray-400 dark:text-neutral-400 hover:text-gray-600 dark:hover:text-neutral-200 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg transition-colors">
                      <Video className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => setShowUserDetails(!showUserDetails)}
                      className="p-2 text-gray-400 dark:text-neutral-400 hover:text-gray-600 dark:hover:text-neutral-200 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                    >
                      <MoreVertical className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <ConversationView
                messages={messages}
                currentUser={currentUser}
                formatTime={formatTime}
              />

              {/* Message Input */}
        <div className="bg-white dark:bg-neutral-900 border-t border-gray-200 dark:border-neutral-800 px-6 py-4 transition-colors">
                <div className="flex items-center space-x-3">
          <button className="p-2 text-gray-400 dark:text-neutral-400 hover:text-gray-600 dark:hover:text-neutral-200 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg transition-colors">
                    <Paperclip className="w-5 h-5" />
                  </button>
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e?.target?.value)}
                      onKeyPress={(e) => e?.key === 'Enter' && sendMessage()}
                      placeholder="Tapez votre message..."
            className="w-full px-4 py-3 border border-gray-200 dark:border-neutral-700 rounded-xl bg-white dark:bg-neutral-800 text-gray-900 dark:text-neutral-100 placeholder:text-gray-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    />
          <button className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 dark:text-neutral-400 hover:text-gray-600 dark:hover:text-neutral-200">
                      <Smile className="w-5 h-5" />
                    </button>
                  </div>
                  <button
                    onClick={sendMessage}
                    disabled={!newMessage?.trim()}
          className="p-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-neutral-900 transition-colors"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
        <MessageCircle className="w-16 h-16 text-gray-300 dark:text-neutral-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 dark:text-neutral-100 mb-2">Sélectionnez une conversation</h3>
        <p className="text-gray-500 dark:text-neutral-400">Choisissez un contact ou une conversation existante pour commencer à échanger</p>
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar - User Details */}
        {showUserDetails && selectedConversation && (
          <UserDetailsPanel
            conversation={selectedConversation}
            onClose={() => setShowUserDetails(false)}
          />
        )}
      </div>
  </div>
  );
};

export default PrivateMessaging;